import type { CmmdProjectRepository } from "@cmmd-forge/contracts";

import { toForgeExecutionTarget, type ForgeExecutionTarget } from "./forgeExecutionTarget";
import type { ProjectRepositoryBindingState } from "./ProjectRepositoryValue";

/**
 * Single source of truth for "what is this CMMD project's Forge execution status?".
 *
 * Distinguishes four cases that the UI previously collapsed into one silent
 * "Not linked":
 * - `no_repo` — the CMMD project has no repository
 * - `repo_ready` — the project has a repository but no Forge execution yet
 * - `linked`  — a Forge execution target is resolvable
 * - `error`   — the repository fetch itself failed (auth/5xx); NOT the same as no_repo
 *
 * Both the global automations index and the project-home loader MUST resolve
 * through this helper rather than re-implementing `getProjectRepository().catch(() => null)`,
 * which masks a transient failure as a genuine "no repo".
 */
export type ProjectExecutionStatus = "no_repo" | "repo_ready" | "linked" | "error";

export interface ProjectExecutionResolution {
  readonly status: ProjectExecutionStatus;
  readonly forgeTarget: ForgeExecutionTarget | null;
  readonly repository: CmmdProjectRepository | null;
}

export async function resolveProjectExecutionStatus(
  projectId: string,
  getProjectRepository: (id: string) => Promise<CmmdProjectRepository>,
): Promise<ProjectExecutionResolution> {
  let repository: CmmdProjectRepository;
  try {
    repository = await getProjectRepository(projectId);
  } catch {
    return { status: "error", forgeTarget: null, repository: null };
  }
  const forgeTarget = toForgeExecutionTarget(repository);
  if (forgeTarget) return { status: "linked", forgeTarget, repository };
  return repository.repoState === "repo_linked"
    ? { status: "repo_ready", forgeTarget: null, repository }
    : { status: "no_repo", forgeTarget: null, repository };
}

/**
 * How the Repository row behaves for a given execution status.
 *
 * The inverse of the Danger zone's gates, from the same one value: as a Forge
 * project comes into existence the repository LOCKS (it is what agents check
 * out) while Forge-project deletion UNLOCKS. Keeping both derivations keyed off
 * `ProjectExecutionStatus` is what stops the two drifting apart.
 *
 * `error` is `unavailable`, never `unattached`: offering an edit we could not
 * verify would let someone re-point a repository whose Forge link failed to read.
 */
export function repositoryBindingStateFor(
  status: ProjectExecutionStatus,
): ProjectRepositoryBindingState {
  switch (status) {
    case "linked":
      return "attached";
    case "error":
      return "unavailable";
    case "no_repo":
    case "repo_ready":
      return "unattached";
  }
}
